/*
 * market-filters — the Filter/Sort bar. Wraps the filter <details> dropdown
 * so clicking outside the panel closes it (native <details> stays open
 * otherwise). The form itself is plain GET — submitting reloads the
 * collection URL with the chosen sort_by + facet params, and the section
 * re-renders from the now-sorted/filtered collection.products. No JS is
 * required for filtering to work; this only handles the open/close UX.
 */
if (!customElements.get('market-filters')) {
  customElements.define(
    'market-filters',
    class MarketFilters extends HTMLElement {
      connectedCallback() {
        this.details = this.querySelector('[data-market-filter]');
        if (!this.details) return;
        this.onDocClick = (e) => {
          if (this.details.open && !this.details.contains(e.target)) {
            this.details.open = false;
          }
        };
        this.onKey = (e) => {
          if (e.key === 'Escape' && this.details.open) {
            this.details.open = false;
            this.details.querySelector('summary')?.focus();
          }
        };
        document.addEventListener('click', this.onDocClick);
        this.addEventListener('keydown', this.onKey);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
      }
    }
  );
}

/*
 * market-spotlight.js — featured product + paginated thumbnail grid.
 * Selecting a thumbnail or arrow swaps the featured image/title/price/link.
 * The thumbnail grid is paginated (CSS grid, configurable columns + rows);
 * navigating featured products beyond the current page auto-shifts the
 * grid to keep the active thumbnail visible. Independent pager prev/next
 * buttons move pages without changing the featured product.
 */
if (!customElements.get('market-spotlight')) {
  customElements.define(
    'market-spotlight',
    class MarketSpotlight extends HTMLElement {
      connectedCallback() {
        this.image = this.querySelector('[data-market-image]');
        this.link = this.querySelector('[data-market-link]');
        this.titleEl = this.querySelector('[data-market-title]');
        this.priceEl = this.querySelector('[data-market-price]');
        this.button = this.querySelector('[data-market-button]');
        this.thumbs = Array.from(this.querySelectorAll('[data-market-thumb]'));
        this.items = Array.from(this.querySelectorAll('.market__thumb-item'));
        this.gridWrap = this.querySelector('[data-market-grid-wrap]');
        this.pageStatus = this.querySelector('[data-market-page-status]');
        this.index = 0;

        if (!this.thumbs.length) return;

        // Pagination state (no-op when only 1 page).
        this.perPage = Number(this.gridWrap?.dataset.perPage) || this.thumbs.length;
        this.totalPages = Number(this.gridWrap?.dataset.totalPages) || 1;
        this.page = 0;

        this.thumbs.forEach((thumb, i) =>
          thumb.addEventListener('click', () => this.select(i))
        );
        this.querySelector('.market__arrow--prev')?.addEventListener('click', () =>
          this.select(this.index - 1)
        );
        this.querySelector('.market__arrow--next')?.addEventListener('click', () =>
          this.select(this.index + 1)
        );

        // Pager: prev / next move pages independently of featured product.
        this.querySelectorAll('[data-market-page]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const dir = btn.dataset.marketPage === 'next' ? 1 : -1;
            this.setPage(this.page + dir);
          });
        });
      }

      select(i) {
        const count = this.thumbs.length;
        this.index = ((i % count) + count) % count;
        const thumb = this.thumbs[this.index];
        const d = thumb.dataset;

        if (this.image && d.image) this.image.src = d.image;
        if (this.titleEl) this.titleEl.textContent = d.title || '';
        if (this.priceEl && d.price) this.priceEl.innerHTML = d.price;
        if (this.link && d.url) this.link.href = d.url;
        if (this.button && d.url) this.button.href = d.url;

        this.thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');

        // Auto-page so the active thumb stays visible in the grid.
        if (this.totalPages > 1) {
          this.setPage(Math.floor(this.index / this.perPage));
        }
      }

      setPage(p) {
        if (this.totalPages <= 1) return;
        // Wrap pages so prev on first goes to last (matches main arrows).
        this.page = ((p % this.totalPages) + this.totalPages) % this.totalPages;
        const pageStr = String(this.page);
        this.items.forEach((item) => {
          item.classList.toggle('is-hidden', item.dataset.page !== pageStr);
        });
        if (this.pageStatus) {
          this.pageStatus.textContent = this.page + 1 + ' / ' + this.totalPages;
        }
      }
    }
  );
}
